import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import type { VenueFilterQuery } from '@lov/shared';
import { useAmenities } from '../../config/hooks/useAmenities';

// Extended filters type to support future amenities and rating filtering
// These fields are not yet in VenueFilterQuery schema but will be added in future
// Use Omit to avoid type conflict (VenueFilterQuery.amenities is string, we need Record<string, boolean>)
type ExtendedFilters = Omit<VenueFilterQuery, 'amenities'> & {
  amenities?: Record<string, boolean>;
  minRating?: number;
};

interface FilterSidebarProps {
  filters: Partial<ExtendedFilters>;
  onFiltersChange: (filters: Partial<ExtendedFilters>) => void;
  onClear: () => void;
}

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 280,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: 24,
  height: '100%',
  overflowY: 'auto',
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.6875rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: theme.palette.text.secondary,
  marginBottom: 12,
}));

const FilterSection = styled(Box)({
  marginBottom: 24,
});

const PriceInputs = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
});

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  '&.Mui-checked': {
    color: theme.palette.success.main,
  },
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
  '&.Mui-checked': {
    color: theme.palette.success.main,
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    fontFamily: '"IBM Plex Sans", sans-serif',
    color: theme.palette.text.primary,
  },
}));

export function FilterSidebar({ filters, onFiltersChange, onClear }: FilterSidebarProps) {
  // Fetch amenities dynamically from API
  const { data: amenityConfigs, isLoading: amenitiesLoading, error: amenitiesError } = useAmenities();

  // Transform AmenityConfig[] to format expected by UI
  const amenities = amenityConfigs?.map(config => ({
    id: config.code,
    label: config.name,
  })) || [];

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined });
  };

  const handlePriceChange = (field: 'minPrice' | 'maxPrice', value: string) => {
    const numValue = value ? Number(value) : undefined;
    onFiltersChange({ ...filters, [field]: numValue });
  };

  const handleAmenityChange = (amenityId: string, checked: boolean) => {
    // Note: The current VenueFilterQuery doesn't support amenity filtering
    // This would need to be added to the schema and backend
    // For now, we'll store it in ExtendedFilters type
    const currentAmenities = filters.amenities || {};
    const updatedAmenities = { ...currentAmenities };

    if (checked) {
      updatedAmenities[amenityId] = true;
    } else {
      delete updatedAmenities[amenityId];
    }

    onFiltersChange({
      ...filters,
      amenities: Object.keys(updatedAmenities).length > 0 ? updatedAmenities : undefined,
    });
  };

  const handleRatingChange = (value: string) => {
    // Note: The current VenueFilterQuery doesn't support rating filtering
    // This would need to be added to the schema and backend
    const minRating = value ? Number(value) : undefined;
    onFiltersChange({ ...filters, minRating });
  };

  const currentAmenities = filters.amenities || {};
  const currentRating = filters.minRating?.toString() || '';

  return (
    <SidebarContainer>
      {/* Search Input */}
      <FilterSection>
        <Input
          placeholder="Search venues..."
          value={filters.search || ''}
          onChange={(e) => handleSearchChange(e.target.value)}
          startIcon={<SearchIcon />}
          size="small"
        />
      </FilterSection>

      <Divider sx={{ mb: 3 }} />

      {/* Price Range */}
      <FilterSection>
        <SectionHeading>Price Range</SectionHeading>
        <PriceInputs>
          <Input
            type="number"
            placeholder="Min"
            value={filters.minPrice || ''}
            onChange={(e) => handlePriceChange('minPrice', e.target.value)}
            size="small"
            min={0}
          />
          <Input
            type="number"
            placeholder="Max"
            value={filters.maxPrice || ''}
            onChange={(e) => handlePriceChange('maxPrice', e.target.value)}
            size="small"
            min={0}
          />
        </PriceInputs>
      </FilterSection>

      <Divider sx={{ mb: 3 }} />

      {/* Amenities */}
      <FilterSection>
        <SectionHeading>Amenities</SectionHeading>
        {amenitiesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : amenitiesError ? (
          <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
            Failed to load amenities. Please try again later.
          </Alert>
        ) : amenities.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No amenities available
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {amenities.map((amenity) => (
              <StyledFormControlLabel
                key={amenity.id}
                control={
                  <StyledCheckbox
                    checked={!!currentAmenities[amenity.id]}
                    onChange={(e) => handleAmenityChange(amenity.id, e.target.checked)}
                    size="small"
                  />
                }
                label={amenity.label}
              />
            ))}
          </Box>
        )}
      </FilterSection>

      <Divider sx={{ mb: 3 }} />

      {/* Rating */}
      <FilterSection>
        <SectionHeading>Minimum Rating</SectionHeading>
        <RadioGroup value={currentRating} onChange={(e) => handleRatingChange(e.target.value)}>
          <StyledFormControlLabel
            value=""
            control={<StyledRadio size="small" />}
            label="Any rating"
          />
          <StyledFormControlLabel
            value="4"
            control={<StyledRadio size="small" />}
            label="4+ stars"
          />
          <StyledFormControlLabel
            value="4.5"
            control={<StyledRadio size="small" />}
            label="4.5+ stars"
          />
        </RadioGroup>
      </FilterSection>

      <Divider sx={{ mb: 3 }} />

      {/* Clear Filters Button */}
      <Button variant="outline" fullWidth onClick={onClear}>
        Clear Filters
      </Button>
    </SidebarContainer>
  );
}
